import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Assistant Hub Store
 *
 * Manages the Assistant Hub UI state including visibility, filters, and user preferences.
 * Tracks recent bot interactions and search history for improved UX.
 *
 * Dependencies:
 * - None (standalone store)
 *
 * Cleared by:
 * - useAuth hook during logout (via clearState)
 *
 * Note: This store maintains UI-specific state like filters, recent searches,
 * and recent bot IDs. The clearState() method resets everything to initial state
 * to ensure a clean experience for the next user session.
 */

interface AssistantHubState {
  // View state
  isVisible: boolean;

  // Filter state
  searchTerm: string;
  selectedDivisions: string[];
  selectedWorkspace: string | null;
  selectedCategory: string;
  sortBy: 'name' | 'recent' | 'popular';

  // Recent bots (store IDs) - client-side UI state
  recentBotIds: string[];

  // Recent searches - client-side UI state
  recentSearches: string[];
}

interface AssistantHubActions {
  // View actions
  setVisible: (visible: boolean) => void;
  toggleVisible: () => void;

  // Filter actions
  setSearchTerm: (term: string) => void;
  setSelectedDivisions: (divisions: string[]) => void;
  setSelectedWorkspace: (workspace: string | null) => void;
  setSelectedCategory: (category: string) => void;
  setSortBy: (sortBy: 'name' | 'recent' | 'popular') => void;
  resetFilters: () => void;

  // Bot actions
  addToRecent: (botId: string) => void;

  // Search actions
  addRecentSearch: (searchTerm: string) => void;
  clearRecentSearches: () => void;

  // Clear all state
  clearState: () => void;
}

const initialState: AssistantHubState = {
  isVisible: false,
  searchTerm: '',
  selectedDivisions: [],
  selectedWorkspace: null,
  selectedCategory: 'all',
  sortBy: 'name',
  recentBotIds: [],
  recentSearches: [],
};

export const useAssistantHubStore = create<
  AssistantHubState & AssistantHubActions
>()(
  devtools(
    persist(
      immer(set => ({
        ...initialState,

        // View actions
        setVisible: visible =>
          set(
            state => {
              state.isVisible = visible;
            },
            false,
            'assistantHub/setVisible'
          ),
        toggleVisible: () =>
          set(
            state => {
              state.isVisible = !state.isVisible;
            },
            false,
            'assistantHub/toggleVisible'
          ),

        // Filter actions
        setSearchTerm: term =>
          set(
            state => {
              state.searchTerm = term;
            },
            false,
            'assistantHub/setSearchTerm'
          ),
        setSelectedDivisions: divisions =>
          set(
            state => {
              state.selectedDivisions = divisions;
            },
            false,
            'assistantHub/setSelectedDivisions'
          ),
        setSelectedWorkspace: workspace =>
          set(
            state => {
              state.selectedWorkspace = workspace;
            },
            false,
            'assistantHub/setSelectedWorkspace'
          ),
        setSelectedCategory: category =>
          set(
            state => {
              state.selectedCategory = category;
            },
            false,
            'assistantHub/setSelectedCategory'
          ),
        setSortBy: sortBy =>
          set(
            state => {
              state.sortBy = sortBy;
            },
            false,
            'assistantHub/setSortBy'
          ),
        resetFilters: () =>
          set(
            state => {
              state.searchTerm = '';
              state.selectedDivisions = [];
              state.selectedWorkspace = null;
              state.selectedCategory = 'all';
              state.sortBy = 'name';
            },
            false,
            'assistantHub/resetFilters'
          ),

        // Bot actions - Immer makes this cleaner
        addToRecent: botId => {
          set(
            state => {
              // Remove if exists
              const idx = state.recentBotIds.indexOf(botId);
              if (idx !== -1) {
                state.recentBotIds.splice(idx, 1);
              }
              // Add to front
              state.recentBotIds.unshift(botId);
              // Keep only 10
              if (state.recentBotIds.length > 10) {
                state.recentBotIds.length = 10;
              }
            },
            false,
            'assistantHub/addToRecent'
          );
        },

        // Search actions - Immer makes this cleaner
        addRecentSearch: searchTerm => {
          set(
            state => {
              // Remove if exists
              const idx = state.recentSearches.indexOf(searchTerm);
              if (idx !== -1) {
                state.recentSearches.splice(idx, 1);
              }
              // Add to front
              state.recentSearches.unshift(searchTerm);
              // Keep only 5
              if (state.recentSearches.length > 5) {
                state.recentSearches.length = 5;
              }
            },
            false,
            'assistantHub/addRecentSearch'
          );
        },
        clearRecentSearches: () =>
          set(
            state => {
              state.recentSearches = [];
            },
            false,
            'assistantHub/clearRecentSearches'
          ),

        // Clear all state
        clearState: () =>
          set(
            state => {
              Object.assign(state, initialState);
            },
            false,
            'assistantHub/clearState'
          ),
      })),
      {
        name: 'assistant-hub-store',
        partialize: state => ({
          // Persist user preferences
          selectedDivisions: state.selectedDivisions,
          selectedWorkspace: state.selectedWorkspace,
          recentBotIds: state.recentBotIds,
          recentSearches: state.recentSearches,
        }),
      }
    )
  )
);
